// src/services/pdf/generateProforma.js
// Factura Proforma — solicita el pago de la señal de 500€.
// NO tiene validez fiscal. Se emite al confirmar contrato.

import { jsPDF } from "jspdf";
import {
    preloadLogos,
    drawMinimalistHeader,
    drawSnapshot,
    drawSectionTitle,
    drawStatusBadge,
    drawExecutiveSummary,
    checkPageBreak,
    drawFooter,
} from "./pdfBranding.js";
import {
    COMPANY,
    COLORS,
    IVA_RATE,
    formatCurrency,
    formatDate,
    todayFormatted,
    generateDocNumber,
    safeFileName,
} from "./pdfConfig.js";

const SIGNAL_AMOUNT = 500; // €€ brutos fijos de señal

/**
 * Genera la Factura Proforma para solicitar el pago de la señal.
 * @param {object} event       - Datos del evento
 * @param {object} contact     - Contacto principal { name, email, phone, nif }
 * @param {object} companyData - Datos de la empresa (CIF, IBAN, etc.)
 * @param {string} docNumber   - Número de documento ya generado/incrementado
 */
export async function generateProformaPDF(event, contact = {}, companyData = {}, docNumber = "") {

    if (!event) return;

    await preloadLogos();
    const doc = new jsPDF({ compress: true });
    const finalDocNumber = docNumber || generateDocNumber("PRO"); 
    const pageWidth = doc.internal.pageSize.getWidth();


    // === A. CABECERA MINIMALIST ===
    let y = drawMinimalistHeader(doc, { docNumber: finalDocNumber, company: companyData });


    // Título del documento
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40, 40, 40);
    doc.text("FACTURA PROFORMA", pageWidth / 2, y, { align: "center" });
    y += 5;

    // === BADGE — No válida fiscalmente ===
    y = drawStatusBadge(doc, y, "Documento informativo · No válido a efectos fiscales · No declare a Hacienda", { type: "warning" });

    y += 5;

    // === B. SNAPSHOT DE PARTES ===
    y = drawSnapshot(doc, y, {
        client: [
            contact.name || event.client_name_legacy || "—",
            `NIF/CIF: ${contact.nif || "________________"}`,
            contact.fiscal_address || "",
            `${contact.fiscal_zip || ""} ${contact.fiscal_city || ""}`.trim() || contact.email || "—",
        ],

        details: [
            `Nº Proforma: ${finalDocNumber}`,
            `Fecha emisión: ${todayFormatted()}`,
            `Fecha límite pago: ${getPaymentDeadline()}`,
        ],
        event: [
            `Fecha evento: ${formatDate(event.date)}`,
            `Tipo: ${capitalize(event.type || "Evento")}`,
            `Ubicación: ${event.city || event.location || "—"}`,
        ],
    });

    y += 8;

    // === C. TABLA DE CONCEPTO ===
    y = drawSectionTitle(doc, y, "CONCEPTO DE LA RESERVA");

    // Cabecera tabla
    const tableX = 15;
    const tableW = pageWidth - 30;
    doc.setFillColor(245, 245, 247);
    doc.rect(tableX, y - 4, tableW, 8, "F");
    doc.setDrawColor(220, 220, 225);
    doc.setLineWidth(0.2);
    doc.rect(tableX, y - 4, tableW, 8, "S");
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80, 80, 90);
    doc.text("DESCRIPCIÓN", tableX + 4, y);
    doc.text("IMPORTE", pageWidth - 20, y, { align: "right" });
    y += 8;

    // Fila principal
    const eventDesc = event.client_name_legacy || capitalize(event.type || "Evento");
    const description = `Reserva de fecha y servicios musicales — ${eventDesc} ${formatDate(event.date)}`;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.TEXT);
    const descLines = doc.splitTextToSize(description, tableW - 50);
    descLines.forEach((line, i) => {
        doc.text(line, tableX + 4, y + i * 5);
    });
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.TEXT);
    doc.text(formatCurrency(SIGNAL_AMOUNT), pageWidth - 20, y, { align: "right" });
    y += Math.max(descLines.length * 5, 8) + 4;

    // Línea separadora
    doc.setDrawColor(...COLORS.PRIMARY);
    doc.setLineWidth(0.176);
    doc.line(tableX, y, pageWidth - 15, y);
    y += 6;

    // === D. RESUMEN ECONÓMICO ===
    // La señal suele tratarse como anticipo de precio total. 
    // Por claridad mostramos el bruto a transferir.
    const signalBase = SIGNAL_AMOUNT / (1 + IVA_RATE);
    const signalIva  = SIGNAL_AMOUNT - signalBase;

    y = drawExecutiveSummary(doc, y, {
        subtotal: formatCurrency(parseFloat(signalBase.toFixed(2))),
        iva: formatCurrency(parseFloat(signalIva.toFixed(2))),
        total: formatCurrency(SIGNAL_AMOUNT),
    });

    y += 5;

    // === E. INSTRUCCIONES DE PAGO ===
    y = checkPageBreak(doc, y, 40);

    doc.setFillColor(250, 250, 252);
    doc.roundedRect(tableX, y, tableW, 32, 2, 2, "F");
    doc.setDrawColor(230, 230, 235);
    doc.setLineWidth(0.2);
    doc.roundedRect(tableX, y, tableW, 32, 2, 2, "S");

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.DARK);
    doc.text("INSTRUCCIONES DE PAGO", tableX + 6, y + 7);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.TEXT);
    doc.text(`Importe a transferir: ${formatCurrency(SIGNAL_AMOUNT)}`, tableX + 6, y + 14);
    doc.setFont("helvetica", "bold");
    doc.text(`IBAN: ${companyData.iban || COMPANY.iban}`, tableX + 6, y + 20);
    doc.setFont("helvetica", "normal");
    doc.text(`Beneficiario: ${companyData.name || COMPANY.name}`, tableX + 6, y + 26);
    doc.text(`Concepto: RESERVA ${finalDocNumber}`, pageWidth - 20, y + 14, { align: "right" });

    doc.setTextColor(...COLORS.PRIMARY);
    doc.setFont("helvetica", "bold");
    doc.text(`Fecha límite: ${getPaymentDeadline()}`, pageWidth - 20, y + 20, { align: "right" });
    y += 38;

    // === F. NOTA LEGAL ===
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(160, 160, 160);
    doc.text(
        "Este documento no constituye una factura a efectos del artículo 2 del RD 1619/2012. " +
        "Al recibir el pago se emitirá la correspondiente Factura de Anticipo con IVA.",
        tableX, y, { maxWidth: tableW }
    );

    // === FOOTER ===
    drawFooter(doc);

    // === GUARDAR ===
    const safeName = safeFileName(event.client_name_legacy);
    doc.save(`Proforma_${safeName}_${finalDocNumber}.pdf`);

}

// ─── Helpers ───────────────────────────────────────────────────────────────
function capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

function getPaymentDeadline() {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}
